export const APIDAE_EVENT_TYPES = ['FETE_ET_MANIFESTATION', 'FESTIVAL', 'EVENEMENT'] as const;

export type ApidaeEventType = (typeof APIDAE_EVENT_TYPES)[number];

export interface ApidaeOuverturePeriod {
  dateDebut?: string;
  dateFin?: string;
  horaireOuverture?: string;
  horaireFermeture?: string;
}

export function isApidaeEventType(type: string | undefined): type is ApidaeEventType {
  if (!type) return false;
  return (APIDAE_EVENT_TYPES as readonly string[]).includes(type.toUpperCase().trim());
}

function parseIsoDate(isoDate: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
}

function parseApidaeTime(time: string): { hours: number; minutes: number } | null {
  const match = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(time.trim());
  if (!match) return null;
  return { hours: Number(match[1]), minutes: Number(match[2]) };
}

/** Période en cours, sinon prochaine période future (événements passés exclus). */
export function pickEventPeriod(
  periods: ApidaeOuverturePeriod[] | undefined,
  todayIso = new Date().toISOString().slice(0, 10)
): ApidaeOuverturePeriod | null {
  if (!periods?.length) return null;

  const valid = periods.filter((p) => p?.dateDebut && p?.dateFin && p.dateFin >= todayIso);
  if (!valid.length) return null;

  const current = valid.filter((p) => p.dateDebut <= todayIso && p.dateFin >= todayIso);
  if (current.length) {
    return current.sort((a, b) => a.dateDebut.localeCompare(b.dateDebut))[0];
  }

  const upcoming = valid
    .filter((p) => p.dateDebut > todayIso)
    .sort((a, b) => a.dateDebut.localeCompare(b.dateDebut));
  return upcoming[0] ?? null;
}

export function formatApidaeTime(time: string | undefined, locale: string): string {
  if (!time) return '';
  const parsed = parseApidaeTime(time);
  if (!parsed) return '';

  const lang = locale.split('-')[0] || 'fr';
  if (lang === 'fr') {
    const minutes = parsed.minutes ? String(parsed.minutes).padStart(2, '0') : '';
    return minutes ? `${parsed.hours}h${minutes}` : `${parsed.hours}h`;
  }

  const date = new Date(1970, 0, 1, parsed.hours, parsed.minutes);
  return date.toLocaleTimeString(lang, {
    hour: 'numeric',
    minute: parsed.minutes ? '2-digit' : undefined,
    hour12: lang === 'en',
  });
}

function capitalizeFirstLetter(text: string): string {
  if (!text) return text;
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function formatSingleDate(isoDate: string, locale: string): string {
  const date = parseIsoDate(isoDate);
  if (!date) return isoDate;
  const lang = locale.split('-')[0] || 'fr';
  const formatted = date.toLocaleDateString(lang, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  return capitalizeFirstLetter(formatted);
}

type DateRangeLabels = {
  sameDay: (date: string) => string;
  range: (start: string, end: string) => string;
  hours: (start: string, end?: string) => string;
};

const DATE_RANGE_LABELS: Record<string, DateRangeLabels> = {
  fr: {
    sameDay: (date) => date,
    range: (start, end) => `Du ${start} au ${end}`,
    hours: (start, end) => (end ? `De ${start} à ${end}` : `À partir de ${start}`),
  },
  en: {
    sameDay: (date) => date,
    range: (start, end) => `${start} – ${end}`,
    hours: (start, end) => (end ? `${start} – ${end}` : `From ${start}`),
  },
  de: {
    sameDay: (date) => date,
    range: (start, end) => `${start} – ${end}`,
    hours: (start, end) => (end ? `${start} – ${end}` : `Ab ${start}`),
  },
};

function getDateRangeLabels(locale: string): DateRangeLabels {
  const lang = locale.split('-')[0] || 'fr';
  return DATE_RANGE_LABELS[lang] ?? DATE_RANGE_LABELS.fr;
}

export interface EventDateParts {
  datePart: string;
  timePart?: string;
}

export function formatEventDateParts(
  period: ApidaeOuverturePeriod | null | undefined,
  locale = 'fr'
): EventDateParts | null {
  if (!period?.dateDebut || !period?.dateFin) return null;

  const labels = getDateRangeLabels(locale);
  const startLabel = formatSingleDate(period.dateDebut, locale);
  const endLabel = formatSingleDate(period.dateFin, locale);

  let datePart: string;
  if (period.dateDebut === period.dateFin) {
    datePart = labels.sameDay(startLabel);
  } else {
    datePart = labels.range(startLabel, endLabel);
  }

  const openTime = formatApidaeTime(period.horaireOuverture, locale);
  const closeTime = formatApidaeTime(period.horaireFermeture, locale);
  const timePart = openTime ? labels.hours(openTime, closeTime || undefined) : undefined;

  return { datePart, timePart };
}

/** @deprecated Préférer formatEventDateParts pour un affichage sur deux lignes */
export function formatEventDateLabel(
  period: ApidaeOuverturePeriod | null | undefined,
  locale = 'fr'
): string | null {
  const parts = formatEventDateParts(period, locale);
  if (!parts) return null;
  if (!parts.timePart) return parts.datePart;
  return `${parts.datePart}\n${parts.timePart}`;
}

export function extractEventPeriodFromApidaeObject(
  o: Record<string, unknown>
): ApidaeOuverturePeriod | null {
  const periodes = (o.ouverture as { periodesOuvertures?: ApidaeOuverturePeriod[] } | undefined)
    ?.periodesOuvertures;
  return pickEventPeriod(periodes);
}
